// Copyright (c) Docugami, Inc. All rights reserved.

import { IUploadStats } from "filemanager/FileUploader";
import { BuildType } from "networking/BuildType";
import { UploadStatus } from "networking/StorageProviders";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { StorageManagerInstance } from "storage/StorageManager";
import { getProviderFriendlyName } from "storage/StorageProviderHelpers";
import { AppState } from "store";
import { useUploadProgress } from "./UploadProgress";
import { isBuildActive } from "store/state_helpers";
const { v4: uuidv4 } = require('uuid');

export interface IExplorerStatusItem {
    id: string;
    timestamp: Date;
    message: string;
    showProgress?: boolean;
    progress?: number;
}

export function useDocSourceGroupStatusItems(): IExplorerStatusItem[] {
    const uploadItems = useExplorerFileUploadStatus();
    const storageProviderUploads = useStorageProviderUploadStatus();

    const statusItems = useMemo((): IExplorerStatusItem[] =>
        [...uploadItems, ...storageProviderUploads].sort(sort),
        [uploadItems, storageProviderUploads]);

    return statusItems;
}

export function useDocSetGroupStatusItems(): IExplorerStatusItem[] {
    const builds = useSelector((state: AppState) => state.organizations.pipelineBuilds.activeBuilds);

    return useMemo((): IExplorerStatusItem[] => {
        const isClustering = isBuildActive(BuildType.Clustering, builds);
        if (isClustering) {
            const item: IExplorerStatusItem = {
                id: uuidv4(),
                message: "Auto Assigning",
                showProgress: true,
                timestamp: new Date()
            }

            return [item];
        }
        else {
            return [];
        }
    }, [builds]);
}

function useExplorerFileUploadStatus(): IExplorerStatusItem[] {
    const [items, setItems] = useState<IExplorerStatusItem[]>([]);
    const { isUploading, uploadStats } = useUploadProgress();
    const [lastUploadStats, setLastUploadStats] = useState<IUploadStats>(uploadStats);

    const { items: transientItems, addItem: addTransientItem } = useTransientStatusItems();

    useEffect(() => {
        const suffix = lastUploadStats.totalCount !== 1 ? 's' : '';
        if (isUploading) {
            setItems(x => [
                {
                    id: "explorerUploadStatus",
                    message: `Uploading ${lastUploadStats.totalCount} file${suffix} from your hard drive`,
                    showProgress: true,
                    progress: lastUploadStats.percentComplete,
                    timestamp: x[0]?.timestamp ?? new Date()
                }
            ]);
        }
        else if (lastUploadStats.totalCount !== 0) {
            setItems([]);

            const item: IExplorerStatusItem = {
                id: uuidv4(),
                message: `${lastUploadStats.succeededCount} of ${lastUploadStats.totalCount} file${suffix} uploaded successfully`,
                showProgress: true,
                progress: 1,
                timestamp: new Date()
            };

            addTransientItem(item, 5);
        }
    }, [isUploading, lastUploadStats, addTransientItem]);

    useEffect(() => {
        setLastUploadStats(uploadStats);
    }, [uploadStats]);

    return useMemo(() => [...items, ...transientItems], [items, transientItems]);
}

export interface ITransientStatusItems {
    items: IExplorerStatusItem[];
    addItem: (item: IExplorerStatusItem, lifetimeSeconds: number) => void;
}

function useTransientStatusItems(): ITransientStatusItems {
    const [items, setItems] = useState<IExplorerStatusItem[]>([]);
    const isLoadedRef = useRef(true);

    useEffect(() => {
        isLoadedRef.current = true
        return () => { isLoadedRef.current = false };
    }, [])

    const addItem = useCallback((item: IExplorerStatusItem, lifetimeSeconds: number) => {
        if (lifetimeSeconds <= 0) {
            return;
        }

        setItems(x => [...x, item]);
        setTimeout(() => {
            if (!!isLoadedRef.current) {
                setItems(x => x.filter(y => y.id !== item.id));
            }
        }, lifetimeSeconds * 1000);
    }, []);

    return {
        items: items,
        addItem: addItem
    }
}

function useStorageProviderUploadStatus(): IExplorerStatusItem[] {
    const [items, setItems] = useState<IExplorerStatusItem[]>([]);
    const { items: transientItems, addItem: addTransientItem } = useTransientStatusItems();

    useEffect(() => {
        const unregisterEventListener = StorageManagerInstance.activeUploads.registerListener(() => onUploadStatusChanged());
        const unregisterUploadStatus = StorageManagerInstance.enableUploadStatus();
        return () => {
            unregisterEventListener();
            unregisterUploadStatus();
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        []);

    function onUploadStatusChanged() {
        const [activeItems, completedItems] = getExplorerStorageProviderUploadStatus();
        setItems(activeItems);

        // When an item completes, we send a transient message with the final status
        for (const item of completedItems) {
            addTransientItem(item, 5);
        }
    }

    return useMemo(() => [...items, ...transientItems], [items, transientItems]);
}

function getExplorerStorageProviderUploadStatus(): [IExplorerStatusItem[], IExplorerStatusItem[]] {
    const activeItems: IExplorerStatusItem[] = [];
    const completedItems: IExplorerStatusItem[] = [];
    for (const item of StorageManagerInstance.activeUploads.items) {
        const hasItems = item.totalCount > 0;
        const suffix = item.totalCount === 1 ? '' : 's';
        const isComplete = item.status === UploadStatus.Complete;
        const isFailed = item.status === UploadStatus.Failed;
        const message = isComplete ?
            `${item.succeededCount} of ${item.totalCount} documents added from ${getProviderFriendlyName(item.providerId)}` :
            isFailed ?
                `Failed to add ${hasItems ? `${item.totalCount} ` : ''}document${suffix} from ${getProviderFriendlyName(item.providerId)}` :
                `Adding ${hasItems ? item.totalCount : ''} document${suffix} from ${getProviderFriendlyName(item.providerId)}`

        const statusItem: IExplorerStatusItem = {
            id: item.instanceId,
            message: message,
            timestamp: isComplete ? new Date() : new Date(item.timeStamp),
            showProgress: !(isComplete || isFailed)
        };

        if (isComplete || isFailed) {
            completedItems.push(statusItem)
        }
        else {
            activeItems.push(statusItem);
        }
    }

    return [activeItems, completedItems];
}

function sort(item1: IExplorerStatusItem, item2: IExplorerStatusItem) {
    const item1Time = item1.timestamp.getTime();
    const item2Time = item2.timestamp.getTime();

    if (item1Time === item2Time) {
        return 0;
    }

    return item1Time > item2Time ? -1 : 1;
}
